import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { EmptyState } from "@/components/StateViews";
import { toast } from "sonner";
import { Loader2, LifeBuoy, Search } from "lucide-react";
import { TicketDetailModal } from "@/pages/Support";

const STATUS_TONES = {
  new: "bg-blue-50 text-blue-700 border-blue-100",
  under_review: "bg-amber-50 text-amber-700 border-amber-100",
  in_progress: "bg-indigo-50 text-indigo-700 border-indigo-100",
  awaiting_user: "bg-orange-50 text-orange-700 border-orange-100",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-100",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
  reopened: "bg-rose-50 text-rose-700 border-rose-100",
};

export default function SupportAdmin() {
  const [tickets, setTickets] = useState([]);
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [opened, setOpened] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (statusFilter) params.set("status", statusFilter);
      const { data } = await api.get(`/admin/support/tickets?${params}`);
      setTickets(data.tickets || []);
      setLabels(data.status_labels || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, statusFilter]);

  return (
    <DashboardLayout>
      <div data-testid="support-admin">
        <div className="mb-8">
          <div className="text-xs font-bold uppercase tracking-widest text-teal-700">مركز الدعم — الإدارة</div>
          <h1 className="mt-2 text-2xl font-heading font-extrabold text-slate-900">تذاكر الدعم الفني</h1>
          <p className="mt-2 text-slate-600">استعرض جميع التذاكر، غيّر حالتها، وردّ على المستخدمين.</p>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="h-4 w-4 text-slate-400 absolute inset-y-0 start-3 my-auto" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث برقم التذكرة أو الاسم أو البريد" className="w-full rounded-lg border border-slate-300 bg-white ps-9 pe-3 py-2.5 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="admin-search" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm" data-testid="status-filter">
            <option value="">كل الحالات</option>
            {Object.entries(labels).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : tickets.length === 0 ? (
          <EmptyState icon={LifeBuoy} title="لا توجد تذاكر" description="لم يفتح أي مستخدم تذكرة تطابق الفلاتر." testid="admin-empty" />
        ) : (
          <div className="rounded-2xl bg-white border border-cream-200 card-shadow overflow-hidden">
            {/* Mobile cards */}
            <ul className="md:hidden divide-y divide-cream-100" data-testid="support-admin-mobile-list">
              {tickets.map((t) => (
                <li key={t.id} onClick={() => setOpened(t)} className="p-4 active:bg-cream-50 cursor-pointer" data-testid={`admin-ticket-mobile-${t.ticket_no}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-xs text-slate-500 arabic-numerals" dir="ltr">#{t.ticket_no}</div>
                      <div className="mt-0.5 font-bold text-slate-900 truncate">{t.title}</div>
                      <div className="mt-1 text-xs text-slate-600 truncate">{t.user_name}</div>
                      <div className="text-[11px] text-slate-400 truncate" dir="ltr">{t.user_email}</div>
                    </div>
                    <span className={`shrink-0 inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border ${STATUS_TONES[t.status] || "bg-slate-100"}`}>{t.status_label}</span>
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-500">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5">{t.request_type}</span>
                    <span className="arabic-numerals">{t.created_at?.slice(0, 10)}</span>
                  </div>
                </li>
              ))}
            </ul>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-cream-50">
                  <tr>
                    <th className="text-start px-6 py-3 font-semibold text-slate-700">رقم التذكرة</th>
                    <th className="text-start px-6 py-3 font-semibold text-slate-700">المستخدم</th>
                    <th className="text-start px-6 py-3 font-semibold text-slate-700">العنوان</th>
                    <th className="text-start px-6 py-3 font-semibold text-slate-700">النوع</th>
                    <th className="text-start px-6 py-3 font-semibold text-slate-700">الحالة</th>
                    <th className="text-start px-6 py-3 font-semibold text-slate-700">التاريخ</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((t) => (
                    <tr key={t.id} onClick={() => setOpened(t)} className="border-t border-cream-100 hover:bg-cream-50/60 cursor-pointer" data-testid={`admin-ticket-${t.ticket_no}`}>
                      <td className="px-6 py-3 font-mono text-slate-700 arabic-numerals" dir="ltr">{t.ticket_no}</td>
                      <td className="px-6 py-3">
                        <div className="font-semibold text-slate-900">{t.user_name}</div>
                        <div className="text-xs text-slate-500" dir="ltr">{t.user_email}</div>
                      </td>
                      <td className="px-6 py-3 text-slate-700">{t.title}</td>
                      <td className="px-6 py-3 text-slate-700 text-xs">{t.request_type}</td>
                      <td className="px-6 py-3">
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold border ${STATUS_TONES[t.status] || "bg-slate-100"}`}>{t.status_label}</span>
                      </td>
                      <td className="px-6 py-3 text-slate-500 text-xs arabic-numerals">{t.created_at?.slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {opened && (
        <TicketDetailModal ticketId={opened.id} isAdmin onClose={() => setOpened(null)} onChanged={load} />
      )}
    </DashboardLayout>
  );
}
